/**
 * <jgd-review-teaser> — switching between review slides and the jump to the
 * Judge.me widget.
 *
 * Rotation: with `data-interval` (ms) and more than one slide, the active
 * slide cross-fades to the next one. Pauses while hovered or focused and
 * while the tab is hidden; disabled for prefers-reduced-motion. All slides
 * are server-rendered and stacked, so switching never changes the size.
 *
 * Arrows (card layout): buttons with `data-teaser-nav="-1|1"` show the
 * previous/next slide and stop the automatic rotation for good.
 *
 * Swipe (touch only): a horizontal swipe over the slides shows the next
 * (swipe left) or previous (swipe right) slide, like the arrows. Vertical
 * scrolling stays with the browser (`touch-action: pan-y` in the CSS); a
 * swipe moves beyond the tap tolerance, so the browser fires no click.
 *
 * Jump: the widget may sit inside a closed accordion row (<details>). A plain
 * anchor jump would land on hidden content in some browsers, so the row is
 * opened before scrolling. Without JS the anchor link still works.
 */

const SWIPE_MIN_DISTANCE = 40;

class JgdReviewTeaser extends HTMLElement {
  #controller = new AbortController();
  /** @type {HTMLElement[]} */
  #slides = [];
  #index = 0;
  #paused = false;
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    const { signal } = this.#controller;
    this.#slides = [...this.querySelectorAll('.jgd-review-teaser__slide')].filter(
      (slide) => slide instanceof HTMLElement
    );
    this.addEventListener('click', this.#handleClick, { signal });
    this.#setupRotation(signal);
    this.#setupSwipe(signal);
  }

  disconnectedCallback() {
    this.#controller.abort();
    window.clearInterval(this.#timer);
  }

  /** @param {AbortSignal} signal */
  #setupRotation(signal) {
    const interval = Number(this.dataset.interval);
    if (this.#slides.length < 2 || !interval) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const pause = () => (this.#paused = true);
    const resume = () => (this.#paused = false);
    this.addEventListener('mouseenter', pause, { signal });
    this.addEventListener('mouseleave', resume, { signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) resume();
      },
      { signal }
    );

    this.#timer = window.setInterval(() => {
      if (this.#paused || document.hidden) return;
      this.#show((this.#index + 1) % this.#slides.length);
    }, interval);
  }

  /** @param {AbortSignal} signal */
  #setupSwipe(signal) {
    if (this.#slides.length < 2) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    this.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'touch') return;
        tracking = true;
        startX = event.clientX;
        startY = event.clientY;
      },
      { signal }
    );

    this.addEventListener(
      'pointerup',
      (event) => {
        if (!tracking) return;
        tracking = false;

        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        if (Math.abs(deltaX) < SWIPE_MIN_DISTANCE || Math.abs(deltaX) < Math.abs(deltaY)) return;

        this.#step(deltaX < 0 ? 1 : -1);
      },
      { signal }
    );

    this.addEventListener('pointercancel', () => (tracking = false), { signal });
  }

  /**
   * Manual navigation (arrows, swipe): stops the automatic rotation for good.
   * @param {number} delta - 1 for next, -1 for previous
   */
  #step(delta) {
    const count = this.#slides.length;
    if (count < 2) return;
    window.clearInterval(this.#timer);
    this.#timer = undefined;
    this.#show((this.#index + delta + count) % count);
  }

  /** @param {number} index */
  #show(index) {
    const current = this.#slides[this.#index];
    const next = this.#slides[index];
    if (!current || !next) return;

    current.classList.remove('is-active');
    current.setAttribute('aria-hidden', 'true');
    next.classList.add('is-active');
    next.removeAttribute('aria-hidden');
    this.#index = index;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const nav = event.target.closest('[data-teaser-nav]');
    if (nav instanceof HTMLElement) {
      this.#step(Number(nav.dataset.teaserNav));
      return;
    }

    const link = event.target.closest('a[href^="#"]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const target = document.getElementById(link.hash.slice(1));
    if (!target) return;

    event.preventDefault();

    const details = target.closest('details');
    if (details && !details.open) details.open = true;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    (details ?? target).scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  };
}

if (!customElements.get('jgd-review-teaser')) {
  customElements.define('jgd-review-teaser', JgdReviewTeaser);
}
